import React, {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {validateSingleInputPipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {assertJSONObject, JSONValue} from 'util/Types';

import StepConfigEditorRouter from './editors/StepConfigEditorRouter';
import makeInitialConfigState from './initial-values/makeInitialConfigState';
import {STEP_CONFIG_SETTINGS} from './step-config-settings/StepConfigSettings';
import {StepConfigEditorContext} from './StepConfigEditorContext';
import {
  ConfigEditorStepType,
  stepTypeIsConfigEditorSupported,
  isUiSchemaForStepTypeV2,
} from './stepTypeIsConfigEditorSupported';

const FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT =
  'The configuration for this transformation is no longer valid.';

/** @package */
export const FAILED_STEP_WARNING_BANNER_TEXT = `${FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT} Please reconfigure it.`;

interface StepConfigEditorPaneProps {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  config: Record<string, JSONValue> | null;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  inputDataFrameSpec: DataFrameSpec;
  clientStateId: string;
  error?: string;
  stepIsFailed?: boolean;
  readOnly?: boolean;
  forceSingleColumnDisplay?: boolean;
}

export default function StepConfigEditorPane({
  stepType,
  stepVersion,
  inputDataFrameSpec,
  config,
  setConfig,
  clientStateId,
  error,
  stepIsFailed,
  readOnly,
  forceSingleColumnDisplay,
}: StepConfigEditorPaneProps): JSX.Element {
  // Memoize so that the context provider doesn't re-render the entire tree every time this component
  // rerenders e.g. due to a state change (also because we'd get an eslint error otherwise)
  const contextValue = useMemo(
    () => ({dataFrameSpec: inputDataFrameSpec, clientStateId}),
    [inputDataFrameSpec, clientStateId]
  );

  if (!stepTypeIsConfigEditorSupported(stepType, stepVersion) || isUiSchemaForStepTypeV2(stepType)) {
    assertAndContinue(false, `StepConfigEditorPane used for ${stepType} v${stepVersion}`);
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No configuration is available." />;
  }

  const errors = [
    ...(error ? [error] : []),
    ...(config
      ? validateSingleInputPipelineStepConfig(stepType, stepVersion, config, inputDataFrameSpec).errors
      : []),
  ];

  const {uiSchema} = STEP_CONFIG_SETTINGS[stepType][stepVersion];

  return (
    <StepConfigEditorContext.Provider value={contextValue}>
      <StepConfigEditorRouter
        configShape={getStepTypeConfigShape(stepType, stepVersion)}
        config={config ?? makeInitialConfigState(stepType, stepVersion)}
        dataTestAttrs={{}}
        onChangeConfig={(config) => {
          // This should be true as long as we have a list of named variables as the step config shape.
          assertJSONObject(config);
          setConfig(config);
        }}
        uiSchema={uiSchema}
        isTopLevel={true}
        disabled={readOnly}
        forceSingleColumnDisplay={forceSingleColumnDisplay}
      />
      <InlineBannerStack
        messages={errors.map((error) => ({bannerType: InlineBannerType.WARNING, text: error}))}
      />
      {stepIsFailed && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={readOnly ? FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT : FAILED_STEP_WARNING_BANNER_TEXT}
        />
      )}
    </StepConfigEditorContext.Provider>
  );
}
